import React, { useEffect, useState } from 'react';
import SpinnerRing, { type SpinnerRingSize } from './SpinnerRing';

const DEFAULT_PHRASES = ['Loading…'];

type CyclingLoaderProps = {
  phrases?: string[];
  /** ms between phrase changes */
  intervalMs?: number;
  /** `sm` for inline rows; `md` for table overlays */
  size?: SpinnerRingSize;
  className?: string;
};

const LABEL_CLASS: Record<NonNullable<CyclingLoaderProps['size']>, string> = {
  xs: 'text-[11px]',
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-sm',
};

export default function CyclingLoader({
  phrases = DEFAULT_PHRASES,
  intervalMs = 2800,
  size = 'md',
  className = '',
}: CyclingLoaderProps): React.ReactElement {
  const activePhrases = phrases.length > 0 ? phrases : DEFAULT_PHRASES;
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    setIndex(0);
    setVisible(true);
  }, [activePhrases.join('\u0001')]);

  useEffect(() => {
    if (activePhrases.length <= 1) return undefined;

    let fadeTimeout: ReturnType<typeof setTimeout> | undefined;
    const timer = setInterval(() => {
      setVisible(false);
      fadeTimeout = setTimeout(() => {
        setIndex((prev) => (prev + 1) % activePhrases.length);
        setVisible(true);
      }, 200);
    }, intervalMs);

    return () => {
      clearInterval(timer);
      if (fadeTimeout) clearTimeout(fadeTimeout);
    };
  }, [activePhrases, intervalMs]);

  const label = activePhrases[index] ?? activePhrases[0];
  const ringSize = size === 'lg' ? 'lg' : size === 'md' ? 'md' : 'sm';

  return (
    <div className={`flex flex-col items-center gap-3 px-4 text-center ${className}`}>
      <SpinnerRing size={ringSize} variant="accent" aria-label={label} />
      <span
        className={`font-medium text-gray-600 dark:text-slate-300 transition-opacity duration-200 ${LABEL_CLASS[size]}`}
        style={{ opacity: visible ? 1 : 0 }}
        aria-live="polite"
      >
        {label}
      </span>
    </div>
  );
}
